import React, { Suspense } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Skeleton,
  Stack,
  Text,
} from '@eluve/components';
import type { PdfPreviewCanvasProps } from '@eluve/pdf-viewer';

import { usePdfContext } from './PdfContext';

export type PdfPreviewDialogProps = {
  /**
   * The pdfjs-backed canvas — `PdfPreviewCanvas` from `@eluve/pdf-viewer`.
   *
   * Injected rather than imported because this module sits on the login path via
   * the `@eluve/blocks` barrel, and the two surfaces need to load pdfjs (~1.3 MB)
   * differently: the web app wraps it in `React.lazy` so it stays out of the
   * login bundle (ENG-313), while the Chrome extension imports it directly —
   * webpack chunking a dynamic `import()` inside the `content_script` graph adds
   * an eager auto-publicPath resolver that throws in a content script and kills
   * the extension outright (ENG-5561).
   *
   * A lazily-supplied component needs no `Suspense` of its own; the dialog keeps
   * the boundary below.
   */
  canvas: React.ComponentType<PdfPreviewCanvasProps>;
};

const LoadingSkeleton = () => (
  <Stack align="center" justify="center" gap={4} className="p-8">
    <Skeleton className="h-[600px] w-[450px]" />
    <Skeleton className="h-4 w-32" />
  </Stack>
);

const ErrorMessage: React.FC<{ message: string }> = ({ message }) => (
  <Stack
    align="center"
    justify="center"
    className="p-8"
    gap={2}
    direction="horizontal"
  >
    <Text inline color="primary">
      {message}
    </Text>
  </Stack>
);

export const PdfPreviewDialog: React.FC<PdfPreviewDialogProps> = ({
  canvas: PdfPreviewCanvas,
}) => {
  const {
    showPreview,
    setShowPreview,
    currentPreviewFile,
    currentPage,
    totalPages,
    loading,
    error,
    onDocumentLoadSuccess,
    textRenderer,
    handlePrevPage,
    handleNextPage,
  } = usePdfContext();
  return (
    <Dialog open={showPreview} onOpenChange={setShowPreview}>
      <DialogContent size="4xl" className="h-[80vh]">
        <DialogHeader>
          <DialogTitle>{currentPreviewFile?.fileName}</DialogTitle>
        </DialogHeader>

        {/*  Navigation */}
        {totalPages > 1 && (
          <Stack
            gap={3}
            justify="center"
            className="border-b pb-4"
            direction="horizontal"
          >
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevPage}
              disabled={currentPage <= 1}
            >
              ← Prev
            </Button>
            <Text inline size="xs" color="secondary">
              Page {currentPage} of {totalPages}
            </Text>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={currentPage >= totalPages}
            >
              Next →
            </Button>
          </Stack>
        )}

        <div className="flex-1 overflow-auto">
          <Stack direction="horizontal" justify="center" align="stretch">
            {loading && <LoadingSkeleton />}

            {error && <ErrorMessage message={error} />}

            {!loading && !error && currentPreviewFile?.fileUrl && (
              <Suspense fallback={<LoadingSkeleton />}>
                <PdfPreviewCanvas
                  fileUrl={currentPreviewFile.fileUrl}
                  currentPage={currentPage}
                  onDocumentLoadSuccess={onDocumentLoadSuccess}
                  textRenderer={textRenderer}
                  loadingSkeleton={<LoadingSkeleton />}
                  renderError={(message) => <ErrorMessage message={message} />}
                />
              </Suspense>
            )}
          </Stack>
        </div>
      </DialogContent>
    </Dialog>
  );
};
